import { useAtomValue } from 'jotai'
import React from 'react'

import { type AiAgentOption, aiAgentsAtom } from '../../atoms'
import type { SwitchboardTranslateFn } from '../../types'
import { text } from '../../utils/text'
import { NodeSelect } from './NodeSelect'

export interface AiAgentSelectorProps {
  /** Current selected agent ID */
  value?: string
  /** Callback when agent selection changes */
  onChange: (agentId: string | undefined) => void
  /** Label for the floating label */
  label?: string
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
  // Note: agents are provided via Jotai atoms (aiAgentsAtom)
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
}

function formatAiAgentOptionLabel(agent: AiAgentOption): string {
  const parts = [agent.name]
  const title = agent.title?.trim()
  if (title) parts.push(title)
  parts.push(agent.id)
  return parts.join(' - ')
}

/**
 * Reusable AI agent selector dropdown component.
 *
 * Data Source: Agents from aiAgentsAtom (Jotai atom)
 * Fetched via: GET /internal/v1/ai-configuration/agents?mango-pbx-id={id}
 * Only agents without a signalsAiPhoneNumber are included (filtered in consuming app).
 */
export const AiAgentSelector: React.FC<AiAgentSelectorProps> = ({
  value = '',
  onChange,
  label: labelProp,
  t,
}) => {
  const label =
    labelProp ?? text(t, 'switchboard.nodes.aiAgent.agentId', 'Agent ID')
  const aiAgents = useAtomValue(aiAgentsAtom)
  const isEmpty = aiAgents.length === 0

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange(e.target.value || undefined)
    },
    [onChange],
  )

  return (
    <NodeSelect
      label={label}
      value={value}
      onChange={handleChange}
      disabled={isEmpty}
    >
      {isEmpty ? (
        <option value="">
          {text(
            t,
            'switchboard.nodes.aiAgent.noAgents',
            'No agents configured',
          )}
        </option>
      ) : (
        <>
          <option value="">
            {text(
              t,
              'switchboard.nodes.aiAgent.selectAgent',
              'Select agent...',
            )}
          </option>
          {aiAgents.map((agent) => (
            <option key={agent.id} value={agent.id}>
              {formatAiAgentOptionLabel(agent)}
            </option>
          ))}
        </>
      )}
    </NodeSelect>
  )
}
